import { Link } from "react-router-dom";
import {
  ArrowUpRight, ArrowDown, Compass, SearchCheck, Activity, Users, LayoutGrid, Clapperboard, Repeat, SlidersHorizontal, BarChart3, MessageCircle,
  ShoppingBag, MessageCircleHeart, MousePointerClick, Megaphone, Smartphone, Award, Brain, Binoculars, LineChart, Handshake, Heart, Send, Bookmark, MoreHorizontal,
} from "lucide-react";
import { Reveal } from "./Reveal";
import { SectionHead } from "./SectionHead";
import { CtaBand } from "./CtaBand";
import { META_WHY, META_STEPS, META_STEPS_INTRO, META_OBJECTIVES, META_WHY_US, META_FINAL } from "@/data/metaAds";

const ICONS = { Compass, SearchCheck, Activity, Users, LayoutGrid, Clapperboard, Repeat, SlidersHorizontal, BarChart3, MessageCircle,
  ShoppingBag, MessageCircleHeart, MousePointerClick, Megaphone, Smartphone, Award, Brain, Binoculars, LineChart, Handshake };
const Icon = ({ name, className }) => { const I = ICONS[name] || Compass; return <I className={className} aria-hidden="true" />; };

const TalkButton = ({ testid, className = "btn-accent" }) => (
  <Link to="/contact" className={className} data-testid={testid}>Let's Talk About Your Ads <ArrowUpRight className="h-4 w-4" /></Link>
);

const FeedPost = ({ tag, title, cta, tone, className = "" }) => (
  <div className={`rounded-[22px] bg-white p-3 shadow-[0_30px_60px_-30px_rgba(19,5,42,.55)] ring-1 ring-violet-100 ${className}`}>
    <div className="flex items-center gap-2.5 px-1">
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-[#7C3AED] to-[#2563EB] text-[11px] font-extrabold text-white">YB</span>
      <span className="leading-tight"><span className="block text-xs font-extrabold text-[#13052A]">Your Business</span><span className="block text-[11px] text-slate-600">Sponsored</span></span>
      <MoreHorizontal className="ml-auto h-4 w-4 text-slate-500" aria-hidden="true" />
    </div>
    <div className={`mt-3 flex aspect-[4/3] flex-col justify-end rounded-2xl p-4 ${tone}`}>
      <span className="self-start rounded-full bg-white/90 px-2.5 py-1 text-[11px] font-bold text-[#581C87]">{tag}</span>
      <p className="mt-2 text-lg font-extrabold leading-tight text-white">{title}</p>
    </div>
    <div className="mt-3 flex items-center justify-between rounded-xl bg-[#F5F0FF] px-3 py-2">
      <span className="text-xs font-bold text-[#13052A]">{cta}</span>
      <ArrowUpRight className="h-4 w-4 text-[#7C3AED]" aria-hidden="true" />
    </div>
    <div className="mt-2 flex items-center gap-3 px-1 text-slate-500">
      <Heart className="h-4 w-4 text-[#FF7043]" aria-hidden="true" /><MessageCircle className="h-4 w-4" aria-hidden="true" /><Send className="h-4 w-4" aria-hidden="true" />
      <Bookmark className="ml-auto h-4 w-4" aria-hidden="true" />
    </div>
  </div>
);

const FeedGraphic = () => (
  <div className="relative mx-auto w-full max-w-[420px]" role="img" aria-label="Illustration of a sponsored Facebook and Instagram advert appearing in a social media feed" data-testid="meta-feed-graphic">
    <div className="pointer-events-none absolute -inset-6 rounded-[40px] bg-gradient-to-br from-[#7C3AED]/25 via-[#2563EB]/15 to-transparent blur-2xl" />
    <div className="relative rounded-[36px] bg-[#13052A] p-3 ring-1 ring-white/10">
      <div className="rounded-[28px] bg-[#FBFAFF] p-3 sm:p-4">
        <div className="mb-3 flex items-center justify-between px-1">
          <span className="text-sm font-extrabold text-[#13052A]">Feed</span>
          <span className="flex gap-1">{[0, 1, 2].map((i) => <span key={i} className="h-1.5 w-1.5 rounded-full bg-violet-300" />)}</span>
        </div>
        <FeedPost tag="New in Tunbridge Wells" title="Book your first session this week" cta="Book now" tone="bg-gradient-to-br from-[#581C87] to-[#2563EB]" />
      </div>
    </div>
    <div className="absolute -bottom-6 -right-2 hidden w-44 rounded-2xl bg-white p-3 shadow-[0_24px_50px_-24px_rgba(19,5,42,.6)] ring-1 ring-violet-100 sm:block">
      <p className="text-[11px] font-bold uppercase tracking-widest text-[#581C87]">Reached while scrolling</p>
      <p className="mt-1 text-xs text-slate-600">Before they've started searching</p>
    </div>
  </div>
);

export const MetaWhy = () => (
  <section className="overflow-hidden bg-white py-24 sm:py-28" data-testid="meta-why">
    <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-[1.15fr_1fr]">
      <div>
        <SectionHead eyebrow={META_WHY.eyebrow} title={META_WHY.title} />
        <Reveal className="mt-6 max-w-2xl space-y-4">
          <p className="text-lg font-bold text-[#13052A] md:text-xl">{META_WHY.lead}</p>
          {META_WHY.paras.map((p) => <p key={p} className="text-base leading-relaxed text-slate-600">{p}</p>)}
          <p className="serif-accent text-xl text-[#581C87] md:text-2xl">{META_WHY.close}</p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-2 text-sm" data-testid="meta-why-links">
            <span className="font-semibold text-slate-600">Works alongside:</span>
            {META_WHY.links.map(([to, label]) => (
              <Link key={to} to={to} className="font-bold text-[#7C3AED] underline-offset-4 hover:underline" data-testid={`meta-why-link${to.replace(/\//g, "-")}`}>{label}</Link>
            ))}
          </div>
          <div className="pt-4"><TalkButton testid="meta-why-cta" /></div>
        </Reveal>
      </div>
      <Reveal delay={0.1} className="pb-6"><FeedGraphic /></Reveal>
    </div>
  </section>
);

const scrollToObjectives = (e) => {
  const el = document.getElementById("campaign-objectives");
  if (!el) return;
  e.preventDefault();
  window.history.replaceState(null, "", "#campaign-objectives");
  const top = el.getBoundingClientRect().top + window.scrollY - 96;
  window.scrollTo({ top, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
};

const Step = ({ s, i }) => (
  <Reveal delay={(i % 2) * 0.05} y={16} className="h-full">
    <li className="flex h-full gap-4 rounded-[24px] border border-violet-100 bg-white p-6 transition-[transform,box-shadow] duration-500 hover:-translate-y-0.5 hover:shadow-[0_24px_50px_-30px_rgba(46,16,101,.35)] sm:gap-5 sm:p-7" data-testid={`meta-step-${i + 1}`}>
      <div className="flex shrink-0 flex-col items-center gap-2">
        <span className="text-sm font-extrabold text-[#7C3AED]">{String(i + 1).padStart(2, "0")}</span>
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#F5F0FF] text-[#581C87]"><Icon name={s.icon} className="h-5 w-5" /></span>
      </div>
      <div>
        <h3 className="text-lg font-extrabold leading-snug text-[#13052A]">{s.title}</h3>
        <div className="mt-2 space-y-2 text-sm leading-relaxed text-slate-600">{s.paras.map((p) => <p key={p}>{p}</p>)}</div>
        {s.objectivesLink && (
          <a href="#campaign-objectives" onClick={scrollToObjectives} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#F5F0FF] px-4 py-2 text-sm font-bold text-[#581C87] transition-colors duration-300 hover:bg-violet-200" data-testid="meta-explore-objectives-link">
            Explore Campaign Objectives <ArrowDown className="h-4 w-4" aria-hidden="true" />
          </a>
        )}
      </div>
    </li>
  </Reveal>
);

export const MetaSteps = () => (
  <section className="bg-[#F5F0FF] py-24 sm:py-28" data-testid="meta-steps">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHead eyebrow="Meta Ads management in Tunbridge Wells & Kent" title="What's Included in Facebook & Instagram Ads Management?" text={META_STEPS_INTRO} />
      <ol className="mt-12 grid gap-4 md:grid-cols-2">{META_STEPS.map((s, i) => <Step key={s.title} s={s} i={i} />)}</ol>
    </div>
  </section>
);

export const MetaObjectives = () => (
  <section id="campaign-objectives" className="scroll-mt-24 bg-white py-24 sm:py-28" data-testid="meta-objectives">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHead eyebrow="Campaign objectives" title={META_OBJECTIVES.title} text={META_OBJECTIVES.intro} />
      <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {META_OBJECTIVES.cards.map((c, i) => (
          <Reveal key={c.title} delay={(i % 3) * 0.05} y={16} className="h-full">
            <div className="group h-full rounded-[24px] border border-violet-100 bg-[#FBFAFF] p-7 transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-1 hover:border-violet-300 hover:shadow-[0_30px_60px_-36px_rgba(46,16,101,.45)]" data-testid={`meta-objective-${i + 1}`}>
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-violet-200 bg-white text-[#7C3AED] transition-colors duration-500 group-hover:bg-[#7C3AED] group-hover:text-white">
                <Icon name={c.icon} className="h-6 w-6" />
              </span>
              <h3 className="mt-5 text-xl font-extrabold text-[#13052A]">{c.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{c.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-10 flex flex-col gap-6 rounded-[28px] bg-[#13052A] p-8 text-white sm:p-10 lg:flex-row lg:items-center lg:justify-between" data-testid="meta-objectives-cta">
        <div className="max-w-2xl">
          <h3 className="text-2xl font-extrabold leading-tight sm:text-3xl">{META_OBJECTIVES.ctaTitle}</h3>
          <p className="mt-3 text-base leading-relaxed text-violet-100">{META_OBJECTIVES.ctaText}</p>
        </div>
        <TalkButton testid="meta-objectives-cta-btn" className="btn-accent shrink-0 self-start lg:self-center" />
      </Reveal>
    </div>
  </section>
);

export const MetaWhyUs = () => (
  <section className="grain relative overflow-hidden bg-[#13052A] py-24 text-white sm:py-28" data-testid="meta-why-us">
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
      <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr]">
        <SectionHead dark eyebrow="Why choose Digital AdDoctor?" title={META_WHY_US.title} />
        <Reveal className="space-y-4 text-base leading-relaxed text-violet-100 md:text-lg">{META_WHY_US.paras.map((p) => <p key={p}>{p}</p>)}</Reveal>
      </div>
      <ul className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {META_WHY_US.points.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.05} y={16} className="h-full">
            <li className="h-full rounded-[22px] bg-white/5 p-6 ring-1 ring-white/10" data-testid={`meta-why-us-${i + 1}`}>
              <Icon name={p.icon} className="h-6 w-6 text-[#C4B5FD]" />
              <h3 className="mt-4 text-base font-extrabold leading-snug">{p.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-violet-100">{p.text}</p>
            </li>
          </Reveal>
        ))}
      </ul>
      <Reveal className="mt-14 max-w-4xl border-l-2 border-[#FF7043] pl-6 sm:pl-8">
        <blockquote data-testid="meta-why-us-quote">
          <p className="text-lg leading-relaxed text-violet-50 md:text-xl">"{META_WHY_US.quote}</p>
          <p className="serif-accent mt-4 text-2xl text-[#D8CCFF] md:text-3xl">{META_WHY_US.quoteClose}"</p>
          <footer className="mt-4 text-sm font-bold text-violet-200">Freya Jones, founder of Digital AdDoctor</footer>
        </blockquote>
      </Reveal>
    </div>
  </section>
);

export const MetaFinalCta = () => (
  <CtaBand title={META_FINAL.title} text={META_FINAL.text}>
    <TalkButton testid="meta-final-cta-talk" />
    <Link to="/#services" className="btn-ghost-dark" data-testid="meta-final-cta-services">Explore My Other Services <ArrowUpRight className="h-4 w-4" /></Link>
  </CtaBand>
);
